/* Importing the createSlice and createAsyncThunk functions from the reduxjs/toolkit library. */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

/* Importing the axios library. */
import axios from '../../api/http/Repository';

/* The initial state of the reducer. */
const initialState = {
  loading: false,
  error: '',
  noOfQuestionnaires: 0,
  Questionnaires: [],
  searchResults: [],
};

/* Creating a thunk that will be used to get a questionnaire from the server. */
export const getQuestionnaire = createAsyncThunk('questionnaire/getQuestionnaire', (questionnaireID) =>
  axios.get(`/fhir/Questionnaire/${questionnaireID}`).then((response) => response.data)
);

/* Creating a thunk that will be used to get all questionnaires from the server. */
export const getQuestionnaireAll = createAsyncThunk('questionnaire/getQuestionnaireAll', () =>
  axios.get(`/fhir/Questionnaire`).then((response) => response.data)
);

/* Creating a thunk that will be used to delete a questionnaire on the server. */
export const deleteQuestionnaire = createAsyncThunk('questionnaire/deleteQuestionnaire', (questionnaireID) =>
  axios.delete(`/fhir/Questionnaire/${questionnaireID}`).then((response) => {
    const data = {
      data: response.data,
      questionnaireID,
    };
    return data;
  })
);

/* Creating a thunk that will be used to post a questionnaire on the server. */
export const postQuestionnaire = createAsyncThunk('questionnaire/postQuestionnaire', (payload) =>
  axios
    .post(`/fhir/Questionnaire`, payload, {
      headers: {
        'content-type': 'application/fhir+json',
      },
    })
    .then((response) => response.data)
);

/* Creating a thunk that will be used to update a questionnaire on the server. */
export const putQuestionnaire = createAsyncThunk('questionnaire/putQuestionnaire', (payload) =>
  axios
    .put(`/fhir/Questionnaire/${payload.questionnaireID}`, payload.data, {
      headers: {
        'Content-Type': 'application/fhir+json',
      },
    })
    .then((response) => response.data)
);

/* Creating a thunk that will be used to search Questionnaire from the server. */
export const searchQuestionnaire = createAsyncThunk('questionnaire/searchQuestionnaire', ({ query, signal }) =>
  axios.get(`/fhir/Questionnaire?${query}`, { signal }).then((response) => response.data)
);

/* Creating a slice of the state. */
const questionnaireSlice = createSlice({
  name: 'questionnaire',
  initialState,
  extraReducers: (builder) => {
    /* deleteQuestionnaire reducers */
    builder.addCase(deleteQuestionnaire.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(deleteQuestionnaire.fulfilled, (state, action) => {
      state.loading = false;
      state.Questionnaires = state.Questionnaires.filter((obj) => obj.id !== action.payload.questionnaireID);
      state.error = '';
      state.noOfQuestionnaires = state.Questionnaires.length;
    });

    builder.addCase(deleteQuestionnaire.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    /* getQuestionnaire reducers */
    builder.addCase(getQuestionnaire.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(getQuestionnaire.fulfilled, (state, action) => {
      state.loading = false;
      state.error = '';

      const data = action.payload;
      if (data.resourceType === 'Questionnaire') {
        const index = state.Questionnaires.findIndex((obj) => obj.id === data.id);
        if (index === -1) {
          state.Questionnaires.push(data);
        } else {
          state.Questionnaires[index] = data;
        }
      } else if (data.resourceType === 'Bundle') {
        if (data.entry?.length > 0) {
          data.entry.forEach((element) => {
            const index = state.Questionnaires.findIndex((obj) => obj.id === element.resource?.id);
            if (index === -1) {
              state.Questionnaires.push(element.resource);
            } else {
              state.Questionnaires[index] = element.resource;
            }
          });
        }
      }
      state.noOfQuestionnaires = state.Questionnaires.length;
    });

    builder.addCase(getQuestionnaire.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    /* getQuestionnaireAll reducers */
    builder.addCase(getQuestionnaireAll.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(getQuestionnaireAll.fulfilled, (state, action) => {
      state.loading = false;
      state.error = '';
      const bundle = action.payload;

      if (bundle.entry?.length > 0) {
        const data = bundle.entry;

        data.forEach((element) => {
          const index = state.Questionnaires.findIndex((obj) => obj.id === element.resource.id);
          if (index === -1) {
            state.Questionnaires.push(element.resource);
          } else {
            state.Questionnaires[index] = element.resource;
          }
        });
      }

      state.noOfQuestionnaires = state.Questionnaires.length;
    });

    builder.addCase(getQuestionnaireAll.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    /* postQuestionnaire reducers */
    builder.addCase(postQuestionnaire.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(postQuestionnaire.fulfilled, (state, action) => {
      state.loading = false;
      state.error = '';
      state.Questionnaires = [...state.Questionnaires, action.payload];
    });

    builder.addCase(postQuestionnaire.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    /* putQuestionnaire reducers */
    builder.addCase(putQuestionnaire.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(putQuestionnaire.fulfilled, (state, action) => {
      state.loading = false;
      state.error = '';

      const index = state.Questionnaires.findIndex((obj) => obj.id === action.payload.id);
      if (index === -1) {
        state.Questionnaires.push(action.payload);
      } else {
        state.Questionnaires[index] = action.payload;
      }
    });

    builder.addCase(putQuestionnaire.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    /* searchQuestionnaire reducers */
    builder.addCase(searchQuestionnaire.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(searchQuestionnaire.fulfilled, (state, action) => {
      state.loading = false;
      state.error = '';
      const bundle = action.payload;

      if (bundle.total > 0) {
        const data = bundle.entry;
        state.searchResults = data;
      }
    });

    builder.addCase(searchQuestionnaire.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
  },
});

/* Exporting the reducer. */
export default questionnaireSlice.reducer;
